class VariantCard extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.currentVariantId = null;
    this.imageElement = null;
    this.priceElement = null;
    this.comparePriceElement = null;
    this.dropdown = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get necessary elements
    this.imageElement = this.querySelector('.custom-card-img img');
    this.priceElement = this.querySelector('.custom-card-price');
    this.comparePriceElement = this.querySelector('.custom-card-compareprice');
    this.dropdown = this.querySelector('.custom-dropdown');

    if (!this.dropdown) return;

    // Get product data from data attribute
    this.product = this.getProductData();
    if (!this.product) return;

    // Get initial variant
    const initialVariantId = this.dropdown.querySelector('[name="id"]')?.value;
    if (initialVariantId) {
      this.currentVariantId = initialVariantId;
    }

    // Listen for dropdown changes
    this.dropdown.addEventListener('click', this.handleDropdownClick.bind(this));

    // Listen for custom variant:change event
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
  }

  getProductData() {
    // Try to get product data from data-product attribute
    const productDataAttr = this.getAttribute('data-product');
    if (productDataAttr) {
      try {
        return JSON.parse(productDataAttr);
      } catch (e) {
        console.warn('Error parsing product data:', e);
      }
    }
    return null;
  }

  handleDropdownClick(event) {
    const clickedItem = event.target.closest('li[data-varid]');
    if (!clickedItem) return;

    const newVariantId = clickedItem.dataset.varid;
    if (newVariantId && newVariantId !== this.currentVariantId) {
      this.currentVariantId = newVariantId;
      this.updateCardContent(newVariantId);
    }
  }

  handleVariantChange(event) {
    // Check if the event comes from this dropdown
    if (!this.dropdown.contains(event.target)) {
      const eventVariantId = event.detail?.variantId;
      const eventElement = event.target?.closest('.custom-dropdown');

      // Only update if the event comes from this card's dropdown
      if (eventElement === this.dropdown && eventVariantId) {
        this.currentVariantId = eventVariantId;
        this.updateCardContent(eventVariantId);
      }
    }
  }

  updateCardContent(variantId) {
    if (!this.product || !variantId) return;

    const variant = this.findVariantById(variantId);
    if (!variant) return;

    this.updateImage(variant);
    this.updatePrice(variant);
  }

  findVariantById(variantId) {
    if (!this.product.variants) return null;
    return this.product.variants.find(v => v.id.toString() === variantId.toString());
  }

  updateImage(variant) {
    if (!this.imageElement || !variant.featured_image) return;

    // Get image URL - handle both objects and strings
    let imageUrl;
    if (typeof variant.featured_image === 'string') {
      imageUrl = variant.featured_image;
    } else if (variant.featured_image.url) {
      imageUrl = variant.featured_image.url;
    } else if (variant.featured_image.src) {
      imageUrl = variant.featured_image.src;
    } else {
      return; // No valid URL found
    }

    const sizedImageUrl = this.getSizedImageUrl(imageUrl, '600x');

    // Update image with smooth transition
    this.imageElement.style.opacity = '0.7';

    setTimeout(() => {
      this.imageElement.src = sizedImageUrl;
      this.imageElement.alt = (variant.featured_image.alt || variant.title || '');
      this.imageElement.style.opacity = '1';
    }, 150);
  }

  updatePrice(variant) {
    if (!this.priceElement || !variant.price) return;

    // Format price
    const formattedPrice = this.formatMoney(variant.price);
    this.priceElement.textContent = formattedPrice;

    // Update compare price if exists
    if (this.comparePriceElement && variant.compare_at_price && variant.compare_at_price > variant.price) {
      const formattedComparePrice = this.formatMoney(variant.compare_at_price);
      this.comparePriceElement.textContent = formattedComparePrice;
      this.comparePriceElement.style.display = 'inline';
    } else if (this.comparePriceElement) {
      this.comparePriceElement.style.display = 'none';
    }
  }

  getSizedImageUrl(url, size) {
    if (!url || typeof url !== 'string') return url;

    // If it already has a size, replace it
    if (url.includes('_') && url.includes('x')) {
      return url.replace(/_\d+x\d*\./, `_${size}.`);
    }

    // If no size, add it before the extension
    const lastDotIndex = url.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      return url.substring(0, lastDotIndex) + '_' + size + url.substring(lastDotIndex);
    }

    return url;
  }

  formatMoney(cents) {
    if (!window.Shopify || !window.Shopify.formatMoney) {
      // Simple fallback if Shopify.formatMoney is not available
      return `$${(cents / 100).toFixed(2)}`;
    }
    return window.Shopify.formatMoney(cents);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.handleVariantChange.bind(this));
  }
}

// Register the custom element
customElements.define('variant-card', VariantCard);